<template>
  <v-col
    v-if="enabled"
    :cols="cols"
    :lg="lg"
    :md="md"
    :sm="sm"
  >
    <v-row dense>
      <v-col
        v-if="label"
        :class="labelClass"
        cols="12"
      >
        <span>
          {{ label }}
        </span>

        <RequiredIndicator v-if="mandatory" />

        <HelpIcon
          :document="helpDocument"
          :on-help-click="openHelpFlyout"
        />
      </v-col>

      <template v-if="description">
        <v-col
          class="fs-16 c-light-black my-1"
          cols="12"
        >
          <span>
            {{ $bct(description) }}
          </span>
        </v-col>
      </template>

      <v-col cols="12">
        <MaskedInput
          v-model="localValue"
          @update:model-value="handleUpdateModelValue"
          v-slot="{ inputRef, masked }"
          :mask="mask || schemaMask"
        >
          <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -->
          <v-text-field
            v-model="masked.value"
            @blur="handleBlur"
            @click:append-inner="handleAppendInnerClick"
            :ref="inputRef"
            :append-inner-icon="appendInnerIconForField"
            :aria-label="ariaLabel"
            :autofocus="autofocus"
            :class="inputClass"
            :counter="showCounter && maxLength"
            :data-testid="inputTestId || 'labeled-text-field'"
            :density="dense || veryDense ? 'compact' : undefined"
            :disabled="locked"
            :message="$bct(message || '')"
            :placeholder="placeholder ? $bct(placeholder) : undefined"
            :prefix="prefix"
            :readonly="readonly"
            :rules="rules"
            :type="effectiveInputType"
            data-cy="labeled-text-field"
            hide-details="auto"
            variant="filled"
            tile
            v-bind="filteredAttrs"
          >
            <template
              v-if="showsPasswordToggle"
              #append-inner
            >
              <v-icon
                @click="handleAppendInnerClick"
                @keyup.enter="handleAppendInnerClick"
                :aria-label="passwordToggleAriaLabel"
                :icon="appendInnerIcon"
                tabindex="0"
              />
            </template>
            <template
              v-for="(_, name) in $slots"
              #[name]="slotData"
            >
              <slot
                :name="name"
                v-bind="slotData"
              />
            </template>
          </v-text-field>
        </MaskedInput>
      </v-col>
    </v-row>

    <v-divider
      v-if="dividedBottom"
      class="mt-4 mb-2"
    />
  </v-col>
</template>

<script setup>
import { onUpdated } from 'vue';

import attrsOmitDataPrefix from '@/shared/utils/filteredAttrs';
import HelpIcon from '@/shared/components/form/HelpIcon.vue';
import MaskedInput from '@/shared/components/form/MaskedInput.vue';
import propsToRefs from '@/shared/utils/propsToRefs';
import RequiredIndicator from '@/shared/components/RequiredIndicator.vue';
import useLabeledField from '@/shared/composables/useLabeledField';
import useSchematizedField from '@/shared/composables/useSchematizedField';

const attrs = useAttrs();
const $bct = inject('translator');

const emit = defineEmits(useLabeledField.emits);
defineExpose({ emit }); // Emit is used in composable, this is included for linter
const localValue = defineModel({ type: [String, Number] });

const props = defineProps({
  dividedBottom: Boolean,
  inputTestId: {
    type: String,
    default: null,
  },
  mask: {
    type: String,
    default: null,
  },
  maxLength: {
    type: Number,
    default: null,
  },
  extraRules: {
    type: Array,
    default: () => [],
  },
  placeholder: {
    type: String,
    default: null,
  },
  readonly: Boolean,
  showCounter: Boolean,
  type: {
    type: String,
    default: null,
  },
  passwordToggleShowLabel: {
    type: String,
    default: 'Show password',
  },
  passwordToggleHideLabel: {
    type: String,
    default: 'Hide password',
  },
  ...useLabeledField.props,
  ...useSchematizedField.props,
});

const ICON_ATTRS_TO_OMIT = ['append-icon', 'prepend-icon'];

function getFilteredAttrs() {
  const filtered = attrsOmitDataPrefix(attrs);
  return Object.fromEntries(
    Object.entries(filtered).filter(([key]) => {
      if (ICON_ATTRS_TO_OMIT.includes(key)) return false;
      if (key === 'type' || key === 'onClick:appendInner' || key === 'onClickAppendInner') {
        return false;
      }
      return true;
    }),
  );
}

const filteredAttrs = ref(getFilteredAttrs());

const { appendIcon, inputClass, labelClass, handleBlur, handleUpdateModelValue } = useLabeledField(
  ...propsToRefs(props, useLabeledField.paramKeys),
);

const { enabled, ariaLabel, helpDocument, label, locked, openHelpFlyout, schemaMask } =
  useSchematizedField(...propsToRefs(props, useSchematizedField.paramKeys));

const passwordVisible = ref(false);

watch(
  () => props.type,
  (nextType) => {
    if (nextType !== 'password') {
      passwordVisible.value = false;
    }
  },
);

const effectiveInputType = computed(() => {
  if (props.type !== 'password' || props.readonly) {
    return props.type;
  }

  return passwordVisible.value ? 'text' : 'password';
});

const appendInnerIcon = computed(() => {
  const baseIcon = appendIcon.value;
  if (baseIcon) {
    return baseIcon;
  }

  if (props.type === 'password' && !props.readonly) {
    return passwordVisible.value ? 'visibility_off' : 'visibility';
  }

  return '';
});

const showsPasswordToggle = computed(() => {
  return props.type === 'password' && !props.readonly && !appendIcon.value;
});

const appendInnerIconForField = computed(() => {
  if (showsPasswordToggle.value) {
    return undefined;
  }

  return appendInnerIcon.value || undefined;
});

const passwordToggleAriaLabel = computed(() => {
  if (!showsPasswordToggle.value) {
    return undefined;
  }

  return passwordVisible.value
    ? $bct(props.passwordToggleHideLabel)
    : $bct(props.passwordToggleShowLabel);
});

function handleAppendInnerClick() {
  if (props.type !== 'password' || props.readonly || locked.value) {
    return;
  }

  passwordVisible.value = !passwordVisible.value;
}

const rules = computed(() => {
  const rulesList = [];

  if (props.mandatory) {
    const rule = (value) => !!value || `${$bct(label.value || '')} ${$bct('is required')}`;
    rulesList.push(rule);
  }

  if (props.extraRules?.length) {
    rulesList.push(...props.extraRules);
  }

  if (props.maxLength) {
    const rule = (value) => {
      if (!value) return true;

      return (
        (value && value.length <= props.maxLength) ||
        `${$bct(label.value || '')} ${$bct('is too long')}`
      );
    };
    rulesList.push(rule);
  }

  return rulesList;
});

// ? attrs is not a reactive object, so we update the filteredAttrs ref on every DOM tree update
onUpdated(() => {
  filteredAttrs.value = getFilteredAttrs();
});
</script>
